import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Share2, Radio, Thermometer, Plane, Download, Send, Loader2, RefreshCw, Activity, AlertTriangle, Film, Droplets, Sun, Moon, Wind, CalendarDays, Zap, Waves, Eye, CloudRain } from "lucide-react";

const TYPE_ICON = { radar: Radio, futurecast: CloudRain, current: Thermometer, feels: Thermometer, dewpoint: Droplets, highs: Sun, lows: Moon, wind: Wind, aviation: Plane, seven: CalendarDays, spc: Zap, impact: Activity, warning: AlertTriangle, tropical: Waves, "tropical-outlook": Eye };
const GROUPS = ["Radar", "Maps", "Forecast", "Alerts", "Tropics"];

const fmtUntil = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", weekday: "short", hour: "numeric", minute: "2-digit" }) : "");

const Btn = ({ children, className = "", ...rest }) => (
  <button
    className={`inline-flex items-center gap-2 rounded-sm px-4 py-2 font-oswald text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50 ${className}`}
    {...rest}
  >
    {children}
  </button>
);

const Chip = ({ active, children, ...rest }) => (
  <button
    type="button"
    className={`rounded-sm border px-3 py-2 font-oswald text-[11px] font-bold uppercase tracking-widest transition-colors ${active ? "border-[#1f63c9] bg-[#1f63c9]/15 text-white" : "border-white/15 text-gray-300 hover:border-white/40"}`}
    {...rest}
  >
    {children}
  </button>
);

export function ShareGraphicsTab() {
  const [catalog, setCatalog] = useState(null);
  const [type, setType] = useState("radar");
  const [format, setFormat] = useState("square");
  const [loop, setLoop] = useState(false);
  const [alertId, setAlertId] = useState("");
  const [cityId, setCityId] = useState("atlanta");
  const [spcDay, setSpcDay] = useState("");
  const [systemId, setSystemId] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [result, setResult] = useState(null);
  const [caption, setCaption] = useState("");
  const [targets, setTargets] = useState({ facebook: false, twitter: false });
  const [posting, setPosting] = useState(false);

  const loadCatalog = async (quiet = false) => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/share/catalog");
      setCatalog(data);
      if (!quiet) toast.success(`${data.alerts?.length || 0} active alert${data.alerts?.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Could not load graphics catalog");
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => { loadCatalog(true); }, []);

  const currentType = catalog?.types.find((t) => t.id === type);
  const videoOnly = !!currentType?.video_only;
  const supportsLoop = !!currentType?.video && !videoOnly;
  const isVideo = videoOnly || (supportsLoop && loop);
  const needsAlert = !!currentType?.needs_alert;
  const option = currentType?.option;
  const allowedFormats = catalog?.formats.filter((f) => !currentType || currentType.formats.includes(f.id)) || [];
  const alerts = catalog?.alerts || [];

  const pickType = (t) => {
    setType(t.id);
    if (!t.video) setLoop(false);
    if (t.option === "system" && !systemId && catalog?.systems?.length) setSystemId(catalog.systems[0].id);
    if (!t.formats.includes(format)) setFormat(t.formats[0]);
    if (t.needs_alert && !alertId && alerts.length) setAlertId(alerts[0].id);
    setResult(null);
  };

  const generate = async () => {
    if (needsAlert && !alertId) return toast.error("Pick an active alert first");
    setRendering(true);
    setResult(null);
    try {
      const { data } = await api.post("/share/render", {
        type, format, video: isVideo,
        alert_id: needsAlert ? alertId : undefined,
        city_id: option === "city" ? cityId : undefined,
        spc_day: option === "spc_day" && spcDay ? spcDay : undefined,
        system_id: option === "system" && systemId ? systemId : undefined,
      });
      setResult(data);
      setCaption(data.caption || "");
      toast.success("Graphic generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Render failed");
    } finally {
      setRendering(false);
    }
  };

  const download = async () => {
    if (!result) return;
    try {
      const res = await fetch(result.url);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = result.file;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(result.url, "_blank");
    }
  };

  const post = async () => {
    const picked = Object.keys(targets).filter((k) => targets[k]);
    if (!picked.length) return toast.error("Pick Facebook and/or X first");
    if (!caption.trim()) return toast.error("Add a caption");
    setPosting(true);
    try {
      const { data } = await api.post("/share/post", { file: result.file, caption, targets: picked });
      const r = data.results || {};
      Object.entries(r).forEach(([plat, out]) => {
        if (out.ok) toast.success(`${plat === "facebook" ? "Facebook" : "X"} posted ✓`);
        else toast.error(`${plat === "facebook" ? "Facebook" : "X"}: ${out.error || "failed"}`);
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  if (!catalog) return <div className="glass rounded-sm p-6 text-sm text-gray-400" data-testid="share-loading">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="share-graphics-tab">
      <div className="glass rounded-sm p-5">
        <div className="flex items-center gap-2 font-oswald text-sm font-bold uppercase tracking-widest text-white">
          <Share2 className="h-4 w-4 text-[#ffd60a]" /> Share Graphics
        </div>
        <p className="mt-1 text-xs text-gray-400">
          Generate an on-air styled graphic from live data, then download it or post straight to Facebook / X. Stills export as high-res PNG; radar loops and Futurecast export as native MP4 video. Pick a group below — Radar, statewide Maps, Forecast (7-day, SPC, Daily Impact), active Alerts and the Tropics.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Controls */}
        <div className="glass space-y-4 rounded-sm p-5">
          <div>
            <div className="mb-2 font-oswald text-[11px] font-bold uppercase tracking-widest text-gray-400">Graphic</div>
            <div className="space-y-2">
              {GROUPS.map((g) => {
                const items = catalog.types.filter((t) => t.group === g);
                if (!items.length) return null;
                return (
                  <div key={g} className="flex flex-wrap items-center gap-2" data-testid={`share-group-${g.toLowerCase()}`}>
                    <span className="w-16 shrink-0 font-oswald text-[10px] font-bold uppercase tracking-widest text-gray-500">{g}</span>
                    {items.map((t) => {
                      const Icon = TYPE_ICON[t.id] || Share2;
                      return (
                        <Chip key={t.id} active={type === t.id} data-testid={`share-type-${t.id}`} onClick={() => pickType(t)}>
                          <span className="inline-flex items-center gap-2"><Icon className="h-3.5 w-3.5" /> {t.label}{t.video_only ? " · MP4" : ""}</span>
                        </Chip>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <div className="mb-2 font-oswald text-[11px] font-bold uppercase tracking-widest text-gray-400">Format</div>
            <div className="flex flex-wrap gap-2">
              {allowedFormats.map((f) => (
                <Chip key={f.id} active={format === f.id} data-testid={`share-format-${f.id}`} onClick={() => { setFormat(f.id); setResult(null); }}>
                  {f.label}
                </Chip>
              ))}
            </div>
          </div>

          {supportsLoop && (
            <label className="flex items-center gap-3 rounded-sm border border-white/10 bg-black/30 px-3 py-2" data-testid="share-loop-toggle">
              <input type="checkbox" checked={loop} onChange={(e) => { setLoop(e.target.checked); setResult(null); }} className="h-4 w-4 accent-[#1f63c9]" />
              <span className="inline-flex items-center gap-2 font-oswald text-[11px] font-bold uppercase tracking-widest text-white"><Film className="h-3.5 w-3.5 text-[#ffd60a]" /> 1-hour radar loop (MP4 video)</span>
            </label>
          )}

          {option === "city" && (
            <div data-testid="share-city-picker">
              <div className="mb-2 font-oswald text-[11px] font-bold uppercase tracking-widest text-gray-400">City</div>
              <select data-testid="share-city-select" value={cityId} onChange={(e) => { setCityId(e.target.value); setResult(null); }} className="w-full rounded-sm border border-white/15 bg-black/40 p-2 text-sm text-white outline-none focus:border-[#1f63c9]">
                {(catalog.cities || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          {option === "spc_day" && (
            <div data-testid="share-spc-picker">
              <div className="mb-2 font-oswald text-[11px] font-bold uppercase tracking-widest text-gray-400">Outlook day</div>
              <select data-testid="share-spc-select" value={spcDay} onChange={(e) => { setSpcDay(e.target.value); setResult(null); }} className="w-full rounded-sm border border-white/15 bg-black/40 p-2 text-sm text-white outline-none focus:border-[#1f63c9]">
                <option value="">Auto (highest Georgia risk)</option>
                {(catalog.spc_days || []).map((d) => <option key={d.day} value={d.day}>{`Day ${d.day} — ${d.risk || "no severe risk"}`}</option>)}
              </select>
            </div>
          )}
          {option === "system" && (
            <div data-testid="share-system-picker">
              <div className="mb-2 font-oswald text-[11px] font-bold uppercase tracking-widest text-gray-400">Tropical system</div>
              {(catalog.systems || []).length ? (
                <select data-testid="share-system-select" value={systemId} onChange={(e) => { setSystemId(e.target.value); setResult(null); }} className="w-full rounded-sm border border-white/15 bg-black/40 p-2 text-sm text-white outline-none focus:border-[#1f63c9]">
                  {catalog.systems.map((x) => <option key={x.id} value={x.id}>{`${x.kind} ${x.name}`}</option>)}
                </select>
              ) : (
                <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2 text-xs text-gray-400" data-testid="share-tropics-quiet">Tropics are quiet — the graphic will say so and show the NHC 7-day outlook.</div>
              )}
            </div>
          )}

          {needsAlert && (
            <div data-testid="share-alert-picker">
              <div className="mb-2 flex items-center justify-between">
                <div className="font-oswald text-[11px] font-bold uppercase tracking-widest text-gray-400">Active NWS alert</div>
                <button type="button" data-testid="share-alerts-refresh" onClick={() => loadCatalog()} disabled={refreshing} className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-white disabled:opacity-50">
                  <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                </button>
              </div>
              {alerts.length ? (
                <select
                  data-testid="share-alert-select"
                  value={alertId}
                  onChange={(e) => { setAlertId(e.target.value); setResult(null); }}
                  className="w-full rounded-sm border border-white/15 bg-black/40 p-2 text-sm text-white outline-none focus:border-[#1f63c9]"
                >
                  {alerts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.event} · {a.counties === 1 ? (a.area || "").split(";")[0] : `${a.counties} counties`} · until {fmtUntil(a.expires)}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="rounded-sm border border-white/10 bg-black/30 px-3 py-2 text-xs text-gray-400" data-testid="share-alerts-empty">No active NWS alerts for Georgia right now.</div>
              )}
            </div>
          )}

          <Btn data-testid="share-generate-btn" onClick={generate} disabled={rendering || (needsAlert && !alerts.length)} className="w-full bg-[#1f63c9] text-white hover:bg-[#2a74d8]">
            {rendering ? <><Loader2 className="h-4 w-4 animate-spin" /> {isVideo ? "Rendering video (≈30s)…" : "Generating…"}</> : <><RefreshCw className="h-4 w-4" /> {isVideo ? "Generate Video" : "Generate Graphic"}</>}
          </Btn>
        </div>

        {/* Preview + publish */}
        <div className="glass space-y-4 rounded-sm p-5">
          <div className="font-oswald text-[11px] font-bold uppercase tracking-widest text-gray-400">Preview</div>
          <div className="flex min-h-[220px] items-center justify-center rounded-sm border border-white/10 bg-black/40 p-3" data-testid="share-preview">
            {result && result.video ? (
              <video src={result.url} controls autoPlay loop muted playsInline className="max-h-[360px] w-auto max-w-full rounded-sm" data-testid="share-preview-video" />
            ) : result ? (
              <img src={`${result.url}?ts=${Date.now()}`} alt="Graphic preview" className="max-h-[360px] w-auto max-w-full rounded-sm" data-testid="share-preview-img" />
            ) : (
              <span className="text-xs text-gray-500">Generate a graphic to preview it here</span>
            )}
          </div>

          {result && (
            <>
              <textarea
                data-testid="share-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={3}
                className="w-full rounded-sm border border-white/15 bg-black/40 p-3 text-sm text-white outline-none focus:border-[#1f63c9]"
                placeholder="Caption for social post…"
              />
              <div className="flex flex-wrap items-center gap-3">
                <Btn data-testid="share-download-btn" onClick={download} className="border border-white/15 text-white hover:border-[#ffd60a]">
                  <Download className="h-4 w-4" /> Download
                </Btn>
                <label className="flex items-center gap-2 text-xs text-gray-300" data-testid="share-target-facebook">
                  <input type="checkbox" checked={targets.facebook} onChange={(e) => setTargets((s) => ({ ...s, facebook: e.target.checked }))} className="h-4 w-4 accent-[#1877f2]" /> Facebook
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-300" data-testid="share-target-twitter">
                  <input type="checkbox" checked={targets.twitter} onChange={(e) => setTargets((s) => ({ ...s, twitter: e.target.checked }))} className="h-4 w-4 accent-white" /> X / Twitter
                </label>
                <Btn data-testid="share-post-btn" onClick={post} disabled={posting} className="bg-[#1d9bf0] text-white hover:bg-[#1a8cd8]">
                  {posting ? <><Loader2 className="h-4 w-4 animate-spin" /> Posting…</> : <><Send className="h-4 w-4" /> Post</>}
                </Btn>
              </div>
              {result.video && (
                <p className="text-[11px] text-gray-500" data-testid="share-video-note">Posts as a native MP4 video on Facebook and X — autoplays in the feed. Download it for Instagram Reels / Stories.</p>
              )}
            </>
          )}
        </div>
      </div>

      <BrandKit />
    </div>
  );
}

const saveBlob = async (url, name) => {
  try {
    const blob = await (await fetch(url)).blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(url, "_blank");
  }
};

function BrandKit() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bust, setBust] = useState(0);

  const load = async (force = false) => {
    setBusy(true);
    try {
      const { data } = await api.get("/share/brand", { params: force ? { force: true } : {} });
      setItems(data.items);
      setBust(Date.now());
      if (force) toast.success("Brand kit re-rendered");
    } catch {
      toast.error("Could not load brand kit");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="glass rounded-sm p-5" data-testid="brand-kit">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 font-oswald text-sm font-bold uppercase tracking-widest text-white">
            <Zap className="h-4 w-4 text-[#ffd60a]" /> Brand Kit
          </div>
          <p className="mt-1 text-xs text-gray-400">Permanent logo files and the Facebook cover photo. These never expire — download anytime.</p>
        </div>
        <Btn data-testid="brand-kit-regenerate" onClick={() => load(true)} disabled={busy} className="border border-white/15 text-gray-200 hover:border-white/40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Re-render
        </Btn>
      </div>
      {!items ? (
        <div className="mt-4 text-xs text-gray-500" data-testid="brand-kit-loading">Loading…</div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {items.map((it) => (
            <div key={it.file} className="rounded-sm border border-white/10 bg-black/30 p-3" data-testid={`brand-item-${it.file.replace(/\.png$/, "")}`}>
              <div className="flex h-36 items-center justify-center overflow-hidden rounded-sm bg-[repeating-conic-gradient(#1f2937_0_25%,#111827_0_50%)] bg-[length:16px_16px]">
                <img src={`${it.url}?v=${bust}`} alt={it.label} className="max-h-full max-w-full object-contain" />
              </div>
              <div className="mt-2 font-oswald text-xs font-bold uppercase tracking-wider text-white">{it.label}</div>
              <div className="text-[11px] text-gray-500">{it.use} · {(it.size / 1024).toFixed(0)} KB</div>
              <button
                type="button"
                data-testid={`brand-download-${it.file.replace(/\.png$/, "")}`}
                onClick={() => saveBlob(it.url, it.file)}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-sm bg-white px-3 py-2 font-oswald text-[11px] font-bold uppercase tracking-widest text-[#0a0f1c] hover:bg-gray-200"
              >
                <Download className="h-3.5 w-3.5" /> Download PNG
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
